import React from "react";
import { Phone, ArrowRight, Mouse, TrendingUp, Activity, Zap } from "lucide-react";
import { HERO, MEDIA, BOOKING_URL } from "../mock";

const Hero = () => {
  return (
    <section id="top" className="relative pt-36 pb-24 px-6 overflow-hidden">
      <div className="cga-glow-bg" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <div className="cga-container relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 text-center lg:text-left">
            <div className="inline-flex cga-pill mb-7 cga-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5eead4] cga-pulse-dot" />
              Real-time intent data • AI growth engine
            </div>

            <h1 className="cga-h1 cga-fade-up">
              <span className="text-white">{HERO.titleLine1}</span>
              <br />
              <span className="text-[#5eead4]">{HERO.titleHighlight}</span>
              <br />
              <span className="text-white">{HERO.titleLine2}</span>
            </h1>

            <p className="cga-body max-w-xl mt-7 cga-fade-up lg:mx-0 mx-auto">
              {HERO.subtitle}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4 cga-fade-up">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cga-cta-primary"
              >
                <Phone className="w-4 h-4" />
                {HERO.primaryCTA}
                <ArrowRight className="w-4 h-4" />
              </a>
              <div className="flex items-center gap-3 text-white/60 text-sm">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-black"
                      style={{
                        background: `linear-gradient(135deg, hsl(${
                          160 + i * 18
                        } 60% 55%), hsl(${175 + i * 10} 55% 40%))`,
                      }}
                    />
                  ))}
                </div>
                <span>2,400+ businesses scaled</span>
              </div>
            </div>
          </div>

          {/* Right: visual */}
          <div className="lg:col-span-5 cga-fade-up">
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-[28px] opacity-60"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 50%, rgba(94,234,212,0.25), transparent 70%)",
                  filter: "blur(20px)",
                }}
              />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] shadow-[0_30px_80px_-30px_rgba(94,234,212,0.45)]">
                <img
                  src={MEDIA.heroDashboard}
                  alt="Live intent analytics"
                  className="w-full h-[420px] object-cover"
                  loading="eager"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%)",
                  }}
                />

                {/* Floating chips */}
                <div className="absolute top-4 left-4 cga-pill text-[11px] py-1 px-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#5eead4] cga-pulse-dot" />
                  LIVE • Buyers detected
                </div>

                <div className="absolute top-4 right-4 cga-pill text-[11px] py-1 px-2.5 bg-emerald-400/10 border-emerald-400/30 text-emerald-300">
                  <TrendingUp className="w-3 h-3" /> +124% MoM
                </div>

                {/* Bottom KPI bar */}
                <div className="absolute left-4 right-4 bottom-4 grid grid-cols-3 gap-2">
                  <KpiCard icon={Activity} label="Match rate" value="92%" />
                  <KpiCard icon={Zap} label="Response" value="<60s" />
                  <KpiCard icon={TrendingUp} label="Closed deals" value="318" />
                </div>
              </div>

              {/* Floating notification card */}
              <div className="hidden md:flex absolute -left-10 bottom-12 items-center gap-3 px-3.5 py-2.5 rounded-xl border border-white/10 bg-black/80 backdrop-blur-md shadow-xl">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#5eead4]/15 border border-[#5eead4]/30">
                  <Zap className="w-4 h-4 text-[#5eead4]" />
                </div>
                <div className="text-xs">
                  <div className="text-white font-semibold">New buyer matched</div>
                  <div className="text-white/55">Financial Advisor • Tampa, FL</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20 flex justify-center">
          <div className="cga-mouse text-white/45">
            <Mouse className="w-6 h-6" />
          </div>
        </div>
      </div>
    </section>
  );
};

const KpiCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-white/10 bg-black/60 backdrop-blur-sm px-3 py-2">
    <div className="flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-widest">
      <Icon className="w-3 h-3 text-[#5eead4]" /> {label}
    </div>
    <div className="text-white font-bold text-base mt-0.5">{value}</div>
  </div>
);

export default Hero;
